import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Wand2, Copy, Check, Trash2, FileDown, Bookmark, Sparkles, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLottery } from "@/hooks/use-lottery-selection";
import { useAuth } from "@/hooks/use-auth";
import { PlanGate } from "@/components/app/PlanGate";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { ticketPrice } from "@/lib/pool-math";
import {
  generateSmart,
  computeSumRange,
  computePairStats,
  DEFAULT_FILTERS,
  type SmartFilters,
  type NumberStat,
} from "@/lib/smart-generator";
import { GameDnaButton } from "@/components/app/GameDnaButton";
import { CheckScopeDialog } from "@/components/app/CheckScopeDialog";
import { AiGeneratorDialog } from "@/components/app/AiGeneratorDialog";
import type { SavedGameScope } from "@/lib/check-scope";

const FILTER_OPTIONS: {
  key: keyof SmartFilters;
  label: string;
  description: string;
  group: "stat" | "freq" | "history" | "manual";
}[] = [
  { key: "parityBalance", label: "Equilíbrio pares/ímpares", description: "Diferença máxima de 2 entre pares e ímpares", group: "stat" },
  { key: "sumInRange", label: "Soma na faixa típica", description: "Mantém a soma das dezenas dentro do desvio histórico", group: "stat" },
  { key: "decadeSpread", label: "Distribuição por décadas", description: "Espalha por pelo menos 3 décadas diferentes", group: "stat" },
  { key: "noLongRuns", label: "Sem sequências longas", description: "Bloqueia 4+ dezenas consecutivas", group: "stat" },
  { key: "preferHot", label: "Priorizar dezenas quentes", description: "Aumenta peso das mais sorteadas (últimos 100)", group: "freq" },
  { key: "includeOverdue", label: "Incluir dezenas atrasadas", description: "Aumenta peso das que não saem há mais tempo", group: "freq" },
  { key: "preferHotPairs", label: "Priorizar duplas frequentes", description: "Favorece pares de dezenas que mais saem juntos no histórico", group: "freq" },
  { key: "includeOverduePairs", label: "Incluir duplas atrasadas", description: "Favorece pares de dezenas que não saem juntos há mais tempo", group: "freq" },
  { key: "avoidPastDraws", label: "Evitar combinações já sorteadas", description: "Bloqueia jogos idênticos a resultados passados", group: "history" },
  { key: "useFixed", label: "Dezenas fixas e excluídas", description: "Define dezenas obrigatórias e proibidas", group: "manual" },
];

const GROUP_LABELS: Record<string, string> = {
  stat: "Filtros estatísticos",
  freq: "Frequência histórica",
  history: "Histórico",
  manual: "Manual",
};

const GeradorInteligenteContent = () => {
  const { config, selected } = useLottery();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<SmartFilters>(DEFAULT_FILTERS);
  const [count, setCount] = useState(config.pick);
  const [qty, setQty] = useState(5);
  const SS_KEY = `lt-gerador-smart-games-${selected}`;
  const [games, setGames] = useState<number[][]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = sessionStorage.getItem(SS_KEY);
      return raw ? (JSON.parse(raw) as number[][]) : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (games.length === 0) sessionStorage.removeItem(SS_KEY);
      else sessionStorage.setItem(SS_KEY, JSON.stringify(games));
    } catch {
      /* ignore */
    }
  }, [games, SS_KEY]);
  const [stats, setStats] = useState<{ attempts: number; rejected: number } | null>(null);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [fixedInput, setFixedInput] = useState("");
  const [excludedInput, setExcludedInput] = useState("");
  const [generating, setGenerating] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);

  // reset count when lottery changes; rehydrate games from sessionStorage for the new lottery
  useEffect(() => {
    setCount(config.pick);
    setStats(null);
    if (typeof window === "undefined") return;
    try {
      const raw = sessionStorage.getItem(`lt-gerador-smart-games-${selected}`);
      setGames(raw ? (JSON.parse(raw) as number[][]) : []);
    } catch {
      setGames([]);
    }
  }, [selected, config.pick]);

  // Fetch stats and past draws once per lottery
  const { data: backendData, isLoading } = useQuery({
    queryKey: ["smart-gen-data", selected],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data: lottery } = await supabase
        .from("lotteries")
        .select("id")
        .eq("slug", selected)
        .maybeSingle();
      if (!lottery) throw new Error("Loteria não encontrada");

      const [statsRes, drawsRes] = await Promise.all([
        supabase.rpc("get_number_stats", { _lottery_slug: selected }),
        supabase
          .from("draws")
          .select("id, draw_numbers(number)")
          .eq("lottery_id", lottery.id)
          .order("contest_number", { ascending: false })
          .limit(1000),
      ]);

      const stats: NumberStat[] = (statsRes.data ?? []).map((s) => ({
        number: s.number,
        frequency: Number(s.frequency),
        delay: s.delay,
      }));

      const pastDraws = (drawsRes.data ?? [])
        .map((d: any) => (d.draw_numbers ?? []).map((dn: any) => dn.number).sort((a: number, b: number) => a - b))
        .filter((arr: number[]) => arr.length > 0);

      // Some loterias (ex.: Dupla Sena) armazenam 2 sorteios por concurso na mesma linha.
      // Quebramos em blocos do tamanho da aposta (config.pick) para que o sumRange e o
      // avoidPastDraws sejam calculados por sorteio individual, e não pela concatenação.
      const pick = config.pick;
      const normalizedDraws: number[][] = [];
      for (const arr of pastDraws) {
        if (arr.length === pick) {
          normalizedDraws.push(arr);
        } else if (arr.length > pick && arr.length % pick === 0) {
          for (let i = 0; i < arr.length; i += pick) {
            normalizedDraws.push(arr.slice(i, i + pick).sort((a, b) => a - b));
          }
        } else {
          normalizedDraws.push(arr);
        }
      }

      return {
        stats,
        pastDraws: normalizedDraws,
        sumRange: computeSumRange(normalizedDraws),
        pairStats: computePairStats(normalizedDraws),
      };
    },
  });

  const enabledCount = useMemo(
    () =>
      Object.entries(filters).filter(([k, v]) => {
        if (k === "fixedNumbers" || k === "excludedNumbers") return false;
        return v === true;
      }).length,
    [filters],
  );

  const parseNumbers = (s: string) =>
    Array.from(
      new Set(
        s
          .split(/[\s,;.\-\n]+/)
          .map((x) => parseInt(x, 10))
          .filter((n) => !Number.isNaN(n) && n >= config.min && n <= config.max),
      ),
    );

  const handleGenerate = () => {
    if (!backendData) return;
    setGenerating(true);
    const c = Math.max(config.minPick, Math.min(config.maxPick, count));
    const q = Math.max(1, Math.min(1000, qty));

    const fixedNumbers = parseNumbers(fixedInput);
    const excludedNumbers = parseNumbers(excludedInput);

    if (filters.useFixed && fixedNumbers.length > c) {
      toast({ title: "Muitas fixas", description: `Você fixou ${fixedNumbers.length} dezenas mas o jogo só tem ${c}.`, variant: "destructive" });
      setGenerating(false);
      return;
    }

    setTimeout(() => {
      const result = generateSmart({
        min: config.min,
        max: config.max,
        pick: c,
        filters: { ...filters, fixedNumbers, excludedNumbers },
        stats: backendData.stats,
        pastDraws: backendData.pastDraws,
        sumRange: backendData.sumRange,
        count: q,
        pairStats: backendData.pairStats,
      });
      setGames(result.games);
      setStats({ attempts: result.attempts, rejected: result.rejected });
      setGenerating(false);
      if (result.games.length < q) {
        toast({
          title: "Filtros muito restritivos",
          description: `Só foi possível gerar ${result.games.length} de ${q} jogos. Tente desativar alguns filtros.`,
          variant: "destructive",
        });
      }
    }, 50);
  };

  const copyOne = (idx: number) => {
    navigator.clipboard.writeText(games[idx].map((n) => String(n).padStart(2, "0")).join(" - "));
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1500);
  };

  const copyAll = () => {
    const text = games.map((g, i) => `Jogo ${i + 1}: ${g.map((n) => String(n).padStart(2, "0")).join(" - ")}`).join("\n");
    navigator.clipboard.writeText(text);
    toast({ title: "Copiado!", description: `${games.length} jogos copiados.` });
  };

  const exportPDF = () => games.length && navigate("/app/exportar", { state: { games } });

  const openSaveDialog = () => {
    if (!user) { toast({ title: "Faça login", variant: "destructive" }); return; }
    if (games.length === 0) return;
    setScopeOpen(true);
  };

  const persistGames = async (scopeInfo: { scope: SavedGameScope; from: number | null; until: number | null; specialScheduleId?: string | null }) => {
    if (!user) return;
    setSaving(true);
    try {
      const { data: lottery } = await supabase.from("lotteries").select("id").eq("slug", selected).maybeSingle();
      if (!lottery) throw new Error("Loteria não encontrada");
      const rows = games.map((g) => ({
        user_id: user.id,
        lottery_id: lottery.id,
        numbers: g,
        cost: ticketPrice(selected, g.length),
        label: `Inteligente · ${new Date().toLocaleDateString("pt-BR")}`,
        check_scope: scopeInfo.scope,
        check_from_contest: scopeInfo.from,
        check_until_contest: scopeInfo.until,
        special_schedule_id: scopeInfo.specialScheduleId ?? null,
      }));
      const { error } = await supabase.from("saved_games").insert(rows);
      if (error) throw error;
      toast({ title: "Jogos salvos!", description: `${games.length} jogos guardados.` });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Erro desconhecido";
      toast({ title: "Erro ao salvar", description: msg, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const grouped = useMemo(() => {
    const out: Record<string, typeof FILTER_OPTIONS> = {};
    FILTER_OPTIONS.forEach((opt) => {
      out[opt.group] = out[opt.group] ?? [];
      out[opt.group].push(opt);
    });
    return out;
  }, []);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.14em] text-primary">
          <Sparkles className="h-3 w-3" /> Recurso Pro
        </div>
        <h1 className="font-serif text-3xl font-light text-foreground">
          Gerador <em className="not-italic text-primary">inteligente</em> · {config.name}
        </h1>
        <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
          Gere jogos com filtros estatísticos baseados no histórico real. Ative os filtros que quiser
          e o sistema combina todos para entregar jogos balanceados.
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Filtros</CardTitle>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">
                {enabledCount} de {FILTER_OPTIONS.length} ativos
              </span>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => {
                  const allOn = enabledCount === FILTER_OPTIONS.length;
                  setFilters((f) => {
                    const next = { ...f };
                    FILTER_OPTIONS.forEach((opt) => {
                      (next as any)[opt.key] = !allOn;
                    });
                    return next;
                  });
                }}
              >
                {enabledCount === FILTER_OPTIONS.length ? "Limpar todos" : "Selecionar todos"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {Object.entries(grouped).map(([group, opts]) => (
            <div key={group}>
              <div className="mb-3 text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                {GROUP_LABELS[group]}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {opts.map((opt) => {
                  const active = filters[opt.key] === true;
                  return (
                    <label
                      key={opt.key}
                      className={cn(
                        "flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors",
                        active
                          ? "border-primary/50 bg-primary/5"
                          : "border-lt-border bg-lt-bg3 hover:border-lt-cream3",
                      )}
                    >
                      <Checkbox
                        checked={active}
                        onCheckedChange={(v) =>
                          setFilters((f) => ({ ...f, [opt.key]: v === true } as SmartFilters))
                        }
                        className="mt-0.5"
                      />
                      <div className="flex-1 space-y-0.5">
                        <div className="text-sm font-medium text-foreground">{opt.label}</div>
                        <div className="text-xs leading-relaxed text-muted-foreground">
                          {opt.description}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}

          {filters.useFixed && (
            <div className="grid gap-4 rounded-md border border-primary/30 bg-primary/5 p-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wider">Dezenas fixas</Label>
                <Input
                  value={fixedInput}
                  onChange={(e) => setFixedInput(e.target.value)}
                  placeholder={`Ex: ${config.min} ${config.min + 5} ${config.min + 12}`}
                  className="mt-1 font-mono"
                />
                <p className="mt-1 text-[10px] text-muted-foreground">
                  Devem aparecer em todos os jogos
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Dezenas excluídas</Label>
                <Input
                  value={excludedInput}
                  onChange={(e) => setExcludedInput(e.target.value)}
                  placeholder="Ex: 13 25"
                  className="mt-1 font-mono"
                />
                <p className="mt-1 text-[10px] text-muted-foreground">Nunca aparecerão</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generate controls */}
      <Card>
        <CardContent className="grid gap-4 pt-6 sm:grid-cols-3">
          <div>
            <Label className="text-xs text-muted-foreground">Números por jogo</Label>
            <Input
              type="number"
              min={config.minPick}
              max={config.maxPick}
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
              className="mt-1"
            />
            <p className="mt-1 text-[10px] text-muted-foreground">
              mín {config.minPick}, máx {config.maxPick} (regra da Caixa)
            </p>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Quantidade de jogos</Label>
            <Input
              type="number"
              min={1}
              max={1000}
              value={qty}
              onChange={(e) => setQty(Number(e.target.value))}
              className="mt-1"
            />
            <p className="mt-1 text-[10px] text-muted-foreground">de 1 a 1000</p>
          </div>
          <div className="flex items-end">
            <Button
              onClick={handleGenerate}
              disabled={isLoading || generating || !backendData}
              className="w-full"
            >
              <Wand2 className="mr-2 h-4 w-4" />
              {isLoading ? "Carregando dados…" : generating ? "Gerando…" : "Gerar jogos"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
        <div className="flex items-start gap-2 text-sm">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div>
            <div className="font-medium text-foreground">Prefere deixar a IA escolher por você?</div>
            <p className="text-xs text-muted-foreground">A IA Premium lê seu histórico e sugere jogos com justificativa.</p>
          </div>
        </div>
        <AiGeneratorDialog numbersPerGame={Math.max(config.minPick, Math.min(config.maxPick, count))} />
      </div>

      {isLoading && <Skeleton className="h-32" />}

      {/* Results */}
      {games.length > 0 && (
        <Card>
          <CardHeader className="flex-row flex-wrap items-center justify-between gap-2 space-y-0">
            <div>
              <CardTitle className="text-base">
                {games.length} {games.length === 1 ? "jogo gerado" : "jogos gerados"}
              </CardTitle>
              {stats && (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {stats.attempts.toLocaleString("pt-BR")} tentativas ·{" "}
                  {stats.rejected.toLocaleString("pt-BR")} rejeitadas pelos filtros
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={copyAll}>
                <Copy className="mr-1 h-3.5 w-3.5" /> Copiar
              </Button>
              <Button size="sm" variant="outline" onClick={openSaveDialog} disabled={saving || !user}>
                <Bookmark className="mr-1 h-3.5 w-3.5" /> {saving ? "Salvando…" : "Salvar"}
              </Button>
              <Button size="sm" onClick={exportPDF}>
                <FileDown className="mr-1 h-3.5 w-3.5" /> Exportar PDF
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setGames([])}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {games.map((g, i) => {
                const sum = g.reduce((a, b) => a + b, 0);
                const evens = g.filter((n) => n % 2 === 0).length;
                return (
                  <div
                    key={i}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-lt-border bg-lt-bg3 px-3 py-2"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-10 font-mono text-xs text-muted-foreground">
                        #{String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {g.map((n) => (
                          <span
                            key={n}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-lt-gold-border bg-lt-gold-bg font-mono text-xs text-foreground"
                          >
                            {String(n).padStart(2, "0")}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                      <span>soma {sum}</span>
                      <span>
                        {evens}p / {g.length - evens}i
                      </span>
                      <Button size="sm" variant="ghost" onClick={() => copyOne(i)}>
                        {copiedIdx === i ? (
                          <Check className="h-4 w-4 text-lt-teal" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </Button>
                      <GameDnaButton numbers={g} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {games.length === 0 && stats && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="flex items-start gap-3 pt-6 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <div>
              Nenhum jogo passou pelos filtros após {stats.attempts.toLocaleString("pt-BR")}{" "}
              tentativas. Tente desativar alguns filtros ou ampliar a faixa de soma.
            </div>
          </CardContent>
        </Card>
      )}
      <CheckScopeDialog
        open={scopeOpen}
        onOpenChange={setScopeOpen}
        lotterySlug={selected}
        lotteryName={config.name}
        onConfirm={async ({ scope, from, until, specialScheduleId }) => {
          await persistGames({ scope, from, until, specialScheduleId });
        }}
      />
    </div>
  );
};

const GeradorInteligente = () => (
  <PlanGate
    tier="pro"
    featureName="Gerador inteligente"
    description="Gere jogos com filtros estatísticos: pares/ímpares, soma, distribuição, dezenas quentes e mais. Disponível no plano Pro."
  >
    <GeradorInteligenteContent />
  </PlanGate>
);

export default GeradorInteligente;